import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { GlobalContext } from 'providers/GlobalProvider';
import {
  ExportHubAPI,
  ExportDatabaseSettings,
  ExportNotificationMethod,
  ExportScreeningFrequency,
  EXPORT_COMMODITY_LENSES,
} from 'api/ExportHubAPI';
import ScreeningSettingsFields from './ScreeningSettingsFields';
import { fetchExportCommodityLenses } from './screeningLenses';

interface Props {
  databaseId: string;
}

/**
 * Screening settings for an Export Hub database (dashboard "Settings" section):
 * re-screening frequency, notification method, and which BITE commodity lenses
 * to screen against. Frequency drives the nightly Automations cron.
 */
const DatabaseScreeningSettings: React.FC<Props> = ({ databaseId }) => {
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [frequency, setFrequency] = useState<ExportScreeningFrequency>('');
  const [dayOfWeek, setDayOfWeek] = useState('Monday');
  const [method, setMethod] = useState<ExportNotificationMethod>('in_app');
  const [email, setEmail] = useState('');
  const [availableLenses, setAvailableLenses] = useState<string[]>([]);
  const [lenses, setLenses] = useState<string[]>([]);
  const [lastScreen, setLastScreen] = useState<string | null>(null);

  useEffect(() => {
    if (!groupId) return undefined;
    let active = true;
    setLoading(true);
    // Load the DB's saved settings and the available lenses together, so the
    // lens picker reconciles saved selection against the live lens list in one
    // pass (no null-vs-empty race).
    Promise.all([
      ExportHubAPI.getDatabaseSettings(databaseId, groupId),
      fetchExportCommodityLenses(),
    ])
      .then(([s, avail]: [ExportDatabaseSettings, string[]]) => {
        if (!active) return;
        const options = avail.length ? avail : [...EXPORT_COMMODITY_LENSES];
        setAvailableLenses(options);
        setFrequency((s.screening_frequency || '') as ExportScreeningFrequency);
        setDayOfWeek(s.screening_day_of_week || 'Monday');
        setMethod(s.notification_method || 'in_app');
        setEmail(s.notifications_email || cur_user?.basics?.email || '');
        // null / empty saved selection means "screen against everything" —
        // reflect that as all options checked.
        setLenses(
          s.screening_lenses && s.screening_lenses.length > 0
            ? s.screening_lenses
            : [...options],
        );
        setLastScreen(s.last_screen_timestamp || null);
      })
      .catch(() =>
        enqueueSnackbar('Failed to load screening settings.', {
          variant: 'error',
        }),
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [databaseId, groupId]);

  const emailNeeded = method === 'email' || method === 'both';

  const save = async () => {
    if (!groupId) return;
    if (lenses.length === 0) {
      enqueueSnackbar('Select at least one BITE Lens to screen against.', {
        variant: 'warning',
      });
      return;
    }
    if (emailNeeded && !email.trim()) {
      enqueueSnackbar('Enter a notification email address.', {
        variant: 'warning',
      });
      return;
    }
    setSaving(true);
    try {
      const updated = await ExportHubAPI.updateDatabaseSettings(databaseId, {
        group_id: groupId,
        screening_frequency: frequency,
        screening_day_of_week: frequency === 'Weekly' ? dayOfWeek : '',
        notification_method: method,
        notifications_email: emailNeeded ? email.trim() : '',
        // All selected = screen against everything (null); a subset narrows.
        screening_lenses:
          lenses.length === availableLenses.length ? null : lenses,
      });
      setLastScreen(updated.last_screen_timestamp || null);
      enqueueSnackbar('Screening settings saved.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to save screening settings.', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='eh-panel eh-settings-panel'>
      <div className='eh-panel-title eh-panel-title-flex'>
        <span>
          <SettingsOutlinedIcon
            style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 6 }}
          />
          Screening Settings
        </span>
        {frequency ? (
          <span className='eh-pill eh-pill-info'>
            Scheduled: {frequency === 'Yearly' ? 'Annually' : frequency}
          </span>
        ) : (
          <span className='eh-pill eh-pill-muted'>Manual only</span>
        )}
      </div>

      {loading ? (
        <div className='eh-detail-note'>Loading settings…</div>
      ) : (
        <>
          <ScreeningSettingsFields
            frequency={frequency}
            setFrequency={setFrequency}
            dayOfWeek={dayOfWeek}
            setDayOfWeek={setDayOfWeek}
            method={method}
            setMethod={setMethod}
            email={email}
            setEmail={setEmail}
            lenses={lenses}
            setLenses={setLenses}
            availableLenses={availableLenses}
          />

          <div className='eh-settings-foot'>
            <span className='eh-detail-note'>
              {lastScreen
                ? `Last screened ${moment(lastScreen).format(
                    'MMM D, YYYY h:mm A',
                  )}`
                : 'Not screened yet.'}
            </span>
            <button
              type='button'
              className='btn-primary'
              onClick={save}
              disabled={saving}
              style={{ opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DatabaseScreeningSettings;
